import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { ProgressRing } from "@/components/contracts/ProgressRing";
import { ReportSection, Panel } from "./ReportSection";
import { cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

const STATUS_TONES = { Excellent: "text-green-500", Bon: "text-green-500", Attention: "text-amber-400", Critique: "text-red-400" };
const RING_COLORS = { Excellent: "#22C55E", Bon: "#22C55E", Attention: "#F5A524", Critique: "#E5484D" };
const URGENCY = {
  critical: { label: "Critique", dot: "#E5484D", cls: "text-red-400" },
  high: { label: tfr("Élevée"), dot: "#F5A524", cls: "text-amber-400" },
  medium: { label: tfr("Moyenne"), dot: "#3B82F6", cls: "text-blue-400" },
  low: { label: "Basse", dot: "#71717A", cls: "text-muted-foreground" },
};

const FactorBar = ({ f, index }) => (
  <div data-testid={`health-factor-${f.key}`}>
    <div className="flex items-center justify-between text-xs mb-1.5">
      <span className="text-muted-foreground">{f.label}</span>
      <span className="tabular-nums font-medium">{f.count > 0 ? f.count : "✓"}</span>
    </div>
    <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
      <motion.div initial={{ width: 0 }} animate={{ width: `${f.score}%` }}
        transition={{ duration: 0.45, delay: index * 0.05, ease: [0.4, 0, 0.2, 1] }}
        className="h-full rounded-full"
        style={{ background: f.score >= 80 ? "#22C55E" : f.score >= 50 ? "#F5A524" : "#E5484D" }} />
    </div>
  </div>
);

export const SectionHealth = ({ health }) => {
  const navigate = useNavigate();
  const h = health || {};
  const status = h.status || tfr("Calcul en attente");

  return (
    <ReportSection number="04" title={tfr("Santé du portefeuille")}
      purpose={tfr("État actuel de la qualité de vos contrats (indépendant de la période sélectionnée).")} testid="section-health">
      <div className="grid lg:grid-cols-5 gap-4">
        <Panel className="lg:col-span-2" testid="health-index-panel">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-5">{tfr("Indice de santé")}</p>
          {h.index == null ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="health-empty">{tfr("Ajoutez des contrats pour calculer l'indice de santé du portefeuille.")}</p>
          ) : (
            <div className="flex items-center gap-6">
              <ProgressRing percent={h.index} size={132} stroke={9} color={RING_COLORS[status] || "#71717A"}>
                <div className="text-center">
                  <p className="text-3xl font-semibold tabular-nums" data-testid="health-index-value">{h.index}</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider">/ 100</p>
                </div>
              </ProgressRing>
              <div>
                <p className={cn("text-lg font-semibold", STATUS_TONES[status])} data-testid="health-status">{status}</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {h.portfolioSize} {h.portfolioSize > 1 ? tfr("contrats actifs évalués sur 5 facteurs pondérés.") : tfr("contrat actif évalué sur 5 facteurs pondérés.")}
                </p>
              </div>
            </div>
          )}
          {(h.factors || []).length > 0 && (
            <div className="space-y-3.5 mt-7">
              {h.factors.map((f, i) => <FactorBar key={f.key} f={f} index={i} />)}
            </div>
          )}
        </Panel>
        <Panel className="lg:col-span-3" testid="health-actions-panel">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-4">
            Contrats nécessitant une action <span className="normal-case font-normal tracking-normal">{tfr("(triés par urgence)")}</span>
          </p>
          {(h.actions || []).length === 0 ? (
            <div className="py-10 text-center" data-testid="health-actions-empty">
              <p className="text-sm text-green-500 font-medium">{tfr("Aucune action requise")}</p>
              <p className="text-xs text-muted-foreground mt-1">{tfr("Votre portefeuille de contrats est à jour.")}</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {h.actions.map((a, i) => {
                const u = URGENCY[a.urgency] || URGENCY.low;
                return (
                  <motion.button key={a.contract_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: Math.min(i * 0.04, 0.3) }}
                    onClick={() => navigate(`/contracts/${a.contract_id}`)}
                    data-testid={`health-action-${a.contract_id}`}
                    className="w-full flex items-center gap-3 rounded-xl border border-white/[0.05] px-4 py-3 text-left hover:border-white/[0.14] hover:bg-white/[0.02] transition-colors group">
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ background: u.dot }} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{a.title}</p>
                      <p className={cn("text-xs mt-0.5", u.cls)}>{a.reason}{a.player_name ? ` · ${a.player_name}` : ""}</p>
                    </div>
                    <span className="text-xs text-muted-foreground hidden sm:block">{u.label}</span>
                    <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-70 transition-opacity shrink-0" />
                  </motion.button>
                );
              })}
            </div>
          )}
        </Panel>
      </div>
    </ReportSection>
  );
};
